import { z } from "zod";
import { LISTINGS, type PropertyListing } from "../../data/listings";

export const LOCAL_LISTINGS_KEY = "corner-gayrimenkul:listings:v1";
export const LISTING_IMAGE_LIMIT = 24;
export const LISTING_IMAGE_MAX_BYTES = 8 * 1024 * 1024;

const dateString = z.string().refine((value) => !Number.isNaN(Date.parse(value)), "Geçerli bir tarih girin.");
const slug = z.string().trim().min(1).max(220).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Bağlantı adresi yalnızca küçük harf, rakam ve tire içerebilir.");
const imageUrl = z.string().max(2_000_000).refine((value) => {
  if (/^data:image\/(?:jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/.test(value)) return true;
  try { return ["https:", "http:"].includes(new URL(value).protocol); } catch { return false; }
}, "Fotoğraf için geçerli bir HTTP(S) görsel adresi kullanın.");

const listingSchema = z.object({
  id: z.string().trim().min(1).max(100).regex(/^[a-zA-Z0-9_-]+$/, "İlan kimliği geçersiz."),
  slug,
  title: z.string().trim().min(3, "İlan başlığı en az 3 karakter olmalı.").max(180),
  description: z.string().trim().max(500),
  fullDescription: z.array(z.string().trim().min(1).max(10_000)).max(30),
  type: z.enum(["satilik", "kiralik"]),
  category: z.enum(["daire", "villa", "arsa", "isyeri", "dukkan", "ofis"]),
  price: z.number().finite().min(0, "Fiyat negatif olamaz.").max(1_000_000_000_000),
  currency: z.string().trim().min(1).max(10),
  ilce: z.string().trim().min(1, "İlçe seçin.").max(80),
  ilceSlug: z.enum(["aladag", "ceyhan", "cukurova", "feke", "imamoglu", "karaisali", "karatas", "kozan", "pozanti", "saimbeyli", "saricam", "seyhan", "tufanbeyli", "yumurtalik", "yuregir"]),
  mahalle: z.string().trim().max(120),
  mahalleSlug: z.union([slug, z.literal("")]),
  address: z.string().trim().max(500),
  roomCount: z.string().trim().max(30).optional(),
  netArea: z.number().finite().min(0).max(100_000_000),
  grossArea: z.number().finite().min(0).max(100_000_000),
  buildingAge: z.string().trim().max(60),
  floor: z.string().trim().max(60),
  totalFloors: z.number().int().min(0).max(200).optional(),
  heating: z.string().trim().max(100),
  balcony: z.boolean(),
  site: z.boolean(),
  siteName: z.string().trim().max(160).optional(),
  featured: z.boolean().optional(),
  zoning: z.string().trim().max(100).optional(),
  deedStatus: z.string().trim().max(100).optional(),
  parcelInfo: z.string().trim().max(100).optional(),
  kaks: z.string().trim().max(30).optional(),
  gabari: z.string().trim().max(30).optional(),
  occupancy: z.string().trim().max(100).optional(),
  images: z.array(imageUrl).max(LISTING_IMAGE_LIMIT, "Bir ilana en fazla 24 fotoğraf eklenebilir."),
  features: z.array(z.string().trim().min(1).max(200)).max(60),
  agent: z.object({
    name: z.string().trim().min(1).max(100),
    whatsapp: z.string().transform(() => "905335487801"),
    title: z.string().trim().min(1).max(150),
    image: imageUrl.optional(),
  }),
  createdAt: dateString,
  updatedAt: dateString.optional(),
  status: z.enum(["published", "draft", "archived"]).default("published"),
  seoTitle: z.string().trim().max(180).optional(),
  seoDescription: z.string().trim().max(500).optional(),
}).superRefine((listing, context) => {
  if (listing.grossArea < listing.netArea) context.addIssue({ code: z.ZodIssueCode.custom, path: ["grossArea"], message: "Brüt alan net alandan küçük olamaz." });
  if (listing.status === "published") {
    if (listing.title.length < 8) context.addIssue({ code: z.ZodIssueCode.custom, path: ["title"], message: "Yayındaki ilan başlığı en az 8 karakter olmalı." });
    if (!listing.mahalle || !listing.mahalleSlug || !listing.address) context.addIssue({ code: z.ZodIssueCode.custom, path: ["address"], message: "Yayınlamadan önce mahalle ve konum açıklamasını girin." });
    if (listing.price <= 0) context.addIssue({ code: z.ZodIssueCode.custom, path: ["price"], message: "Yayındaki ilan için fiyat sıfırdan büyük olmalı." });
    if (listing.netArea <= 0 || listing.grossArea <= 0) context.addIssue({ code: z.ZodIssueCode.custom, path: ["netArea"], message: "Yayındaki ilan için alan bilgilerini girin." });
    if (listing.images.length === 0) context.addIssue({ code: z.ZodIssueCode.custom, path: ["images"], message: "Yayınlamadan önce en az bir fotoğraf ekleyin." });
    if (listing.description.length < 20 || listing.fullDescription.length === 0) context.addIssue({ code: z.ZodIssueCode.custom, path: ["description"], message: "Yayınlamadan önce kısa açıklama ve detaylı açıklama ekleyin." });
  }
});

export function validateListing(value: unknown): PropertyListing {
  const result = listingSchema.safeParse(value);
  if (!result.success) throw new Error(result.error.issues[0]?.message ?? "İlan bilgileri geçersiz.");
  return result.data as PropertyListing;
}

export interface ListingStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function seedListings(): PropertyListing[] {
  return LISTINGS.map((listing) => validateListing(listing));
}

export function readLocalListings(storage: ListingStorage): PropertyListing[] {
  let raw: string | null;
  try { raw = storage.getItem(LOCAL_LISTINGS_KEY); } catch { throw new Error("Tarayıcı depolamasına erişilemiyor. Tarayıcı ayarlarını kontrol edin."); }
  if (!raw) return seedListings();
  try {
    const parsed = JSON.parse(raw);
    if (parsed.version !== 1 || !Array.isArray(parsed.listings)) throw new Error();
    const listings: PropertyListing[] = parsed.listings.map(validateListing);
    if (new Set(listings.map((listing) => listing.id)).size !== listings.length || new Set(listings.map((listing) => listing.slug)).size !== listings.length) throw new Error();
    return listings;
  } catch { throw new Error("Yerel ilan kayıtları okunamıyor. Kayıtları yedekleyip tarayıcı depolamasını kontrol edin."); }
}

export function writeLocalListings(storage: ListingStorage, listings: PropertyListing[]): void {
  const checked = listings.map(validateListing);
  try { storage.setItem(LOCAL_LISTINGS_KEY, JSON.stringify({ version: 1, listings: checked })); }
  catch { throw new Error("İlan kaydedilemedi: tarayıcı depolama alanı dolu veya erişim engelli. Fotoğraf sayısını azaltın ya da Firebase bağlantısını tamamlayın."); }
}

export function upsertListing(listings: PropertyListing[], listing: PropertyListing): PropertyListing[] {
  const checked = validateListing(listing);
  if (listings.some((item) => item.id !== checked.id && item.slug === checked.slug)) throw new Error("Bu bağlantı adresi başka bir ilanda kullanılıyor.");
  const index = listings.findIndex((item) => item.id === checked.id);
  return index === -1 ? [checked, ...listings] : listings.map((item, position) => position === index ? checked : item);
}

